import { useRef, useState } from "react";
import InlineTitleEditor from "./InlineTitleEditor";
import ProgressBar from "./ProgressBar";
import RowMenu from "./RowMenu";
import { PROGRESS_TYPES } from "./goalTypes";
import type { GoalNode, GoalPatch, ProgressType } from "../types";
import { num, pct } from "../lib/format";

interface GoalRowProps {
  goal: GoalNode;
  /** One channel for every edit: title, target, unit, status, progress type. */
  onEdit: (goal: GoalNode, patch: GoalPatch) => void;
  onDelete: (goal: GoalNode) => void;
  onLogProgress: (goal: GoalNode, value: number) => void;
  onAddStep: (goal: GoalNode, text: string) => void;
  // The step handlers take the goal as well as the step: the page previews the
  // percentage this tick produces, and that is computed from the whole goal.
  onToggleStep: (goal: GoalNode, stepId: string, done: boolean) => void;
  onDeleteStep: (goal: GoalNode, stepId: string) => void;
}

/**
 * "144 / 300 юзерів" for numeric goals, nothing for a plain checkbox.
 *
 * Empty for a percent goal too: its raw value *is* the percentage the row
 * already prints, and "65% · 65%" reads as two different numbers that happen
 * to agree.
 */
function valueText(goal: GoalNode): string | null {
  if (goal.progress_type === "checkbox" || goal.progress_type === "percent") return null;
  if (goal.progress_type === "checklist") {
    const done = goal.checklist.filter((s) => s.done).length;
    return `${done} / ${goal.checklist.length}`;
  }
  return `${num(goal.current_val)} / ${num(goal.target_val)}${goal.unit ? ` ${goal.unit}` : ""}`;
}

/**
 * A text field that saves itself when you leave it.
 *
 * Every field in this row had a «Зберегти» button next to it, which is one
 * button per field and one decision per edit. The row is remounted by `key`
 * whenever the stored value changes, so there is no prop-into-state effect.
 */
function SelfSavingField({
  initial,
  onCommit,
  className = "",
  ...rest
}: {
  initial: string;
  onCommit: (value: string) => void;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onBlur">) {
  const [text, setText] = useState(initial);
  const commit = () => {
    if (text.trim() !== initial.trim()) onCommit(text.trim());
  };
  return (
    <input
      {...rest}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className={`jv-input text-sm ${className}`}
    />
  );
}

/**
 * One goal: tick it off, see how far along it is, open it to edit.
 *
 * The row stays a single line until it is opened, and the panel underneath
 * shows only the control this goal's own type needs — a slider for a percent,
 * two numbers for a target, sub-items for a checklist, nothing at all for a
 * yes/no. A dozen goals with every control permanently expanded is the shape
 * the old app had, and it is why nothing could be found on it.
 *
 * The name is not one of those controls: it is the row's own text, and a
 * double-click (or «Редагувати») turns that text into the field. It used to be
 * a separate input at the top of the panel, so renaming a goal meant reading
 * its name twice on the same row.
 */
export default function GoalRow({
  goal,
  onEdit,
  onDelete,
  onLogProgress,
  onAddStep,
  onToggleStep,
  onDeleteStep,
}: GoalRowProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [step, setStep] = useState("");
  const stepRef = useRef<HTMLInputElement>(null);
  const [dragged, setDragged] = useState<number | null>(null);

  const dropped = goal.status === "dropped";
  const tone = goal.done ? "ok" : "accent";
  const text = valueText(goal);
  // Closed by hand: the bar is full because you said the goal is finished, and
  // printing «100%» next to «4 / 12 книг» would argue with itself. The count
  // stays beside it — closing a goal early is worth seeing.
  const closedByHand = goal.status === "done";
  const sliderValue = dragged ?? goal.current_val;

  // A goal is done either because you said so or because its numbers say so,
  // and only the first can be undone with a checkbox. Unticking "12 / 12 книг"
  // would set the status back to active and change nothing on screen, because
  // the progress still reads 100% — a dead control. Say so instead, and point
  // at the field that actually reopens it, one line below.
  const autoDone = goal.done && goal.status !== "done";

  return (
    // Three tiers, so a mixed list reads at a glance: active at full strength,
    // finished softened, abandoned faded.
    <li
      className={`border-b border-line transition-opacity duration-300 last:border-0 ${
        dropped ? "opacity-50" : goal.done ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-center gap-2.5 py-2.5">
        <input
          type="checkbox"
          checked={goal.done}
          disabled={autoDone}
          onChange={() => onEdit(goal, { status: goal.done ? "active" : "done" })}
          title={
            autoDone ? "Виконана за прогресом — зміни значення нижче, щоб відкрити" : undefined
          }
          aria-label={
            autoDone
              ? `«${goal.title}» виконана за прогресом`
              : `Позначити «${goal.title}» виконаною`
          }
          className="size-4 shrink-0 accent-[var(--color-accent)] disabled:opacity-60"
        />

        <div className="flex min-w-0 flex-1 flex-col">
          {editing ? (
            <InlineTitleEditor
              initial={goal.title}
              label={`Назва цілі «${goal.title}»`}
              onDone={(title) => {
                setEditing(false);
                if (title !== null) onEdit(goal, { title });
              }}
            />
          ) : (
            // The title is the goal's own text until you double-click it, and
            // then it is the field — the same gesture the task panel uses, and
            // «Редагувати» in «⋯» for a touch screen. The open panel used to
            // carry a second copy of the title in an input of its own, which
            // meant the name appeared twice on a row being renamed.
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              onDoubleClick={() => setEditing(true)}
              aria-expanded={open}
              className={`block min-w-0 truncate text-left text-sm transition-colors duration-300 ${
                goal.done ? "text-muted line-through" : "text-fg"
              }`}
            >
              {goal.title}
            </button>
          )}
          {/* The bar opens the row too — it is half of what a thumb aims at —
              but it is not a second tab stop for the same action. Out of the
              tab order, not out of the accessibility tree: the bar carries the
              goal's `progressbar` and the «33% · 4 / 12 книг» beside it, and
              `aria-hidden` here would take the number off the row entirely. */}
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setOpen((v) => !v)}
            aria-label={`Розгорнути «${goal.title}»`}
            className="mt-1 flex items-center gap-2 text-left"
          >
            <ProgressBar
              value={goal.progress_pct}
              tone={tone}
              height={4}
              className="max-w-40 flex-1"
            />
            {closedByHand ? (
              <span className="shrink-0 text-xs text-ok">Виконано</span>
            ) : (
              <span className="jv-num shrink-0 text-xs text-muted">
                {pct(goal.progress_pct, 0)}
              </span>
            )}
            {text && <span className="jv-num shrink-0 text-xs text-muted">· {text}</span>}
          </button>
        </div>

        <RowMenu
          label={`Дії для «${goal.title}»`}
          actions={[
            { label: "Редагувати", onSelect: () => setEditing(true) },
            {
              label: dropped ? "Повернути в роботу" : "Закинути",
              onSelect: () => onEdit(goal, { status: dropped ? "active" : "dropped" }),
            },
            {
              label: "Видалити",
              confirm: "Точно видалити?",
              danger: true,
              onSelect: () => onDelete(goal),
            },
          ]}
        />
      </div>

      {open && (
        <div className="flex flex-col gap-3 pb-3 pl-6.5">
          {goal.progress_type === "number" && (
            <div className="flex items-center gap-2">
              <SelfSavingField
                key={`current-${goal.current_val}`}
                initial={num(goal.current_val)}
                onCommit={(value) => {
                  const parsed = Number(value.replace(",", "."));
                  if (Number.isFinite(parsed)) onLogProgress(goal, parsed);
                }}
                inputMode="decimal"
                aria-label="Поточне значення"
                className="w-20"
              />
              <span className="text-sm text-muted">/</span>
              <SelfSavingField
                key={`target-${goal.target_val}`}
                initial={num(goal.target_val)}
                onCommit={(value) => {
                  const parsed = Number(value.replace(",", "."));
                  if (Number.isFinite(parsed) && parsed > 0) onEdit(goal, { target_val: parsed });
                }}
                inputMode="decimal"
                aria-label="Ціль"
                className="w-20"
              />
              <SelfSavingField
                key={`unit-${goal.unit}`}
                initial={goal.unit}
                onCommit={(value) => onEdit(goal, { unit: value })}
                maxLength={20}
                placeholder="од."
                aria-label="Одиниця виміру"
                className="w-20"
              />
            </div>
          )}

          {goal.progress_type === "percent" && (
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={sliderValue}
                onChange={(e) => setDragged(Number(e.target.value))}
                // Committing on release rather than on every pixel: a drag
                // across the bar is one intention, not a hundred writes.
                onPointerUp={() => {
                  if (dragged !== null) onLogProgress(goal, dragged);
                  setDragged(null);
                }}
                onKeyUp={() => {
                  if (dragged !== null) onLogProgress(goal, dragged);
                  setDragged(null);
                }}
                aria-label="Відсоток виконання"
                className="min-w-0 flex-1 accent-[var(--color-accent)]"
              />
              <span className="jv-num w-10 shrink-0 text-right text-xs text-muted">
                {pct(sliderValue, 0)}
              </span>
            </div>
          )}

          {goal.progress_type === "checklist" && (
            <>
              {goal.checklist.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {goal.checklist.map((item) => (
                    <li key={item.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => onToggleStep(goal, item.id, !item.done)}
                        aria-label={`Пункт «${item.text}»`}
                        className="size-3.5 shrink-0 accent-[var(--color-accent)]"
                      />
                      <span
                        className={`min-w-0 flex-1 text-xs break-words ${
                          item.done ? "text-muted line-through" : "text-fg"
                        }`}
                      >
                        {item.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => onDeleteStep(goal, item.id)}
                        aria-label={`Видалити пункт «${item.text}»`}
                        className="shrink-0 px-1 text-xs text-muted transition-colors hover:text-danger"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!step.trim()) return;
                  onAddStep(goal, step.trim());
                  setStep("");
                  // A checklist is written in one sitting, not one item per
                  // visit. The field is no longer disabled while the write is
                  // in flight, so the cursor simply stays where it was.
                  stepRef.current?.focus();
                }}
              >
                <input
                  ref={stepRef}
                  value={step}
                  onChange={(e) => setStep(e.target.value)}
                  placeholder="+ пункт"
                  aria-label="Новий пункт"
                  className="jv-input w-full text-sm"
                />
              </form>
            </>
          )}

          <TypeSwitch
            current={goal.progress_type}
            onPick={(next) => onEdit(goal, { progress_type: next })}
          />
        </div>
      )}
    </li>
  );
}

/** Four chips to change how this goal is finished, after the fact. */
function TypeSwitch({
  current,
  onPick,
}: {
  current: ProgressType;
  onPick: (type: ProgressType) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Як відмічати" className="flex flex-wrap gap-1">
      {PROGRESS_TYPES.map((option) => {
        const active = option.key === current;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={active}
            title={option.hint}
            onClick={() => onPick(option.key)}
            className={`inline-flex items-center gap-1.5 rounded-control border px-2 py-1 text-micro transition-colors ${
              active
                ? "border-accent bg-accent-soft text-accent"
                : "border-line text-muted hover:border-accent/50 hover:text-fg disabled:opacity-50"
            }`}
          >
            <option.Icon className="size-3.5" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
